import { Brain, MessageSquare, ScanLine, ShieldCheck, Sparkles, Users } from "lucide-react";

const aiCapabilities = [
  {
    icon: MessageSquare,
    title: "Asesor fiscal conversacional",
    description:
      "Pregunta en lenguaje natural. La IA responde con base en la ley española y tu situación real.",
  },
  {
    icon: ScanLine,
    title: "OCR + clasificación inteligente",
    description: "Sube facturas y tickets: la IA los lee, categoriza y los deja listos para Hacienda.",
  },
  {
    icon: ShieldCheck,
    title: "Cumplimiento automático",
    description: "Modelos 130/303, VeriFactu y alertas antes de que algo se escape.",
  },
];

const AISection = () => {
  return (
    <section id="ai" className="relative overflow-hidden bg-[#12082a] py-20 md:py-28">
      <div className="hero-mesh pointer-events-none absolute inset-0 opacity-30" aria-hidden />
      <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-taxby-purple/30 blur-3xl" aria-hidden />

      <div className="container relative z-10 mx-auto px-4">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-sm font-medium text-white/90">
            <Sparkles className="h-4 w-4 text-amber-200" />
            El corazón de Taxby es la IA
          </span>
          <h2 className="mb-5 text-3xl font-bold tracking-tight text-white md:text-4xl lg:text-5xl">
            Asesoría fiscal que piensa contigo
          </h2>
          <p className="text-lg leading-relaxed text-white/75">
            Combinamos gestores expertos con un motor de IA entrenado en fiscalidad española. Menos
            Excel, menos dudas, más claridad en cada trimestre.
          </p>
        </div>

        <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {aiCapabilities.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="group rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all hover:border-taxby-purple/40 hover:bg-white/10"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-taxby-purple/25 transition-transform group-hover:scale-105">
                <Icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-white">{title}</h3>
              <p className="text-sm leading-relaxed text-white/70">{description}</p>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-white/10 to-white/5 p-8 md:p-10">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <div className="mb-6 flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-3">
                  <Brain className="h-5 w-5 text-taxby-purple" />
                  <div>
                    <p className="text-xs text-white/60">Motor IA</p>
                    <p className="text-sm font-semibold text-white">Normativa ES</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-3">
                  <Users className="h-5 w-5 text-emerald-300" />
                  <div>
                    <p className="text-xs text-white/60">Respaldo</p>
                    <p className="text-sm font-semibold text-white">Gestores reales</p>
                  </div>
                </div>
              </div>
              <h3 className="mb-4 text-2xl font-bold text-white md:text-3xl">
                IA que ejecuta. Humanos que respaldan.
              </h3>
              <p className="mb-6 text-white/75">
                La IA hace el trabajo repetitivo — clasificar, calcular, avisar — y el equipo fiscal
                interviene en casos complejos, migraciones o revisiones críticas. Lo mejor de los dos
                mundos.
              </p>
              <ul className="space-y-2 text-sm text-white/85">
                {[
                  "Respuestas con contexto de tu actividad",
                  "Previsiones de impuestos en tiempo real",
                  "Detección de errores antes de presentar",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="text-emerald-300">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative rounded-2xl border border-white/10 bg-[#1a1035] p-5 shadow-2xl">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-white/50">
                Flujo en la app
              </p>
              <div className="space-y-3">
                {[
                  { step: "01", text: "Subes una factura → OCR extrae datos en segundos" },
                  { step: "02", text: "La IA la clasifica y valida deducibilidad" },
                  { step: "03", text: "El asesor responde dudas sobre modelos o plazos" },
                  { step: "04", text: "Presentación lista: 130, 303, VeriFactu" },
                ].map((row) => (
                  <div
                    key={row.step}
                    className="flex gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5"
                  >
                    <span className="text-xs font-bold text-taxby-purple">{row.step}</span>
                    <p className="text-sm text-white/90">{row.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISection;
